<script setup>
import { computed, defineEmits, onBeforeUnmount, ref } from "vue";
import useModal from "@/composables/useModal";
import {
  shippingAddressResendOtp,
  shippingAddressSendOtp,
  shippingAddressVerifyOtp,
} from "@/api";
import VoidAnchor from "@/components/links/VoidAnchor.vue";

const emits = defineEmits(["submit", "close"]);

const { alert } = useModal();

const isLoading = ref(false);
const phone = ref("010");
const secret = ref("");
const expires = ref(0);
const answer = ref("");

let spendInterval = null;
const spendSeconds = ref(0);
const expireSeconds = computed(
  () =>
    Math.floor((expires.value - new Date().getTime()) / 1000) -
    spendSeconds.value * 0
);
const remainingSeconds = computed(() => {
  const seconds = `${Math.max(expireSeconds.value, 0) % 60}`.padStart(2, "0");
  const minutes = `${Math.floor(
    (Math.max(expireSeconds.value, 0) - seconds) / 60
  )}`.padStart(2, "0");

  return `${minutes}:${seconds}`;
});

onBeforeUnmount(() => {
  clearTimer();
});

function startTimer() {
  clearTimer();

  spendInterval = setInterval(() => {
    spendSeconds.value++;
  }, 1000);
}

function clearTimer() {
  spendSeconds.value = 0;
  if (spendInterval !== null) {
    clearInterval(spendInterval);
    spendInterval = null;
  }
}

async function onSendClick() {
  if (isLoading.value) {
    return;
  }

  const _phone = phone.value;
  if (_phone.length !== 11) {
    await alert("올바른 핸드폰번호를 입력해주세요.");
    return;
  }

  isLoading.value = true;

  try {
    const { secret: _secret, expires: _expires } = (
      await shippingAddressSendOtp(_phone)
    ).data.data;

    secret.value = _secret;
    expires.value = _expires;
    startTimer();
  } catch (e) {
    if (e.response.data.status === "GSOASCD401016") {
      alert("핸드폰번호가 일치하지 않습니다.");
      return;
    }
    if (e.response.data.status === "GSOASCD401014") {
      alert("OTP는 1분이후에 재발급 가능 합니다.");
      return;
    }

    alert("인증번호 발급에 실패했습니다.");
  } finally {
    isLoading.value = false;
  }
}

async function onResendClick() {
  if (isLoading.value) {
    return;
  }

  const _secret = secret.value;
  if (!_secret) {
    return;
  }

  isLoading.value = true;

  try {
    const { secret: __secret, expires: __expires } = (
      await shippingAddressResendOtp(_secret)
    ).data.data;
    secret.value = __secret;
    expires.value = __expires;
    startTimer();
  } catch (e) {
    console.error(e);
    if (e.response.data.status === "GSOASCD401012") {
      alert("잘못된 접근입니다.");
      return;
    }
    if (e.response.data.status === "GSOASCD401014") {
      alert("OTP는 1분이후에 재발급 가능 합니다.");
      return;
    }

    alert("인증번호 발급에 실패했습니다.");
  } finally {
    isLoading.value = false;
  }
}

async function onVerifyClick() {
  if (isLoading.value) {
    return;
  }

  const _secret = secret.value;
  const _answer = answer.value;
  if (!_secret || !_answer) {
    await alert("인증번호를 입력해주세요.");
    return;
  }

  isLoading.value = true;

  try {
    const vk = (await shippingAddressVerifyOtp(_secret, _answer)).data.data;

    clearTimer();

    emits("submit", vk);
  } catch (e) {
    if (e.response.data.status === "GSOASCD401013") {
      alert("OTP가 만료되었습니다.");
      secret.value = "";
      expires.value = 0;
      answer.value = "";
      return;
    }
    if (e.response.data.status === "GSOASCD401012") {
      alert("OTP 정보가 일치하지 않습니다.");
      return;
    }

    alert("오류가 발생하였습니다.");
    secret.value = "";
    expires.value = 0;
    answer.value = "";
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="popup-bg on"></div>
  <div class="popup-cont w01 on" id="popup-goods">
    <div class="popup-header">
      <h3 class="title">본인인증</h3>
      <button type="button" class="popup-close-btn" @click="$emit('close')">
        닫기
      </button>
    </div>
    <div class="popup-body">
      <p class="goods_title">인증번호 발송</p>
      <div class="popup_write type-02">
        <div class="input_wrap">
          <form @submit.prevent="onSubmit">
            <div class="input_area">
              <div class="input_group">
                <input
                  type="tel"
                  v-model="phone"
                  :disabled="isLoading || !!secret"
                />
                <void-anchor
                  v-if="!secret"
                  class="btn btn_light_gray_solid primary"
                  :disabled="isLoading || !!secret || phone.length !== 11"
                  @click="onSendClick"
                  >발송</void-anchor
                >
                <void-anchor
                  v-else
                  class="btn btn_light_gray_solid"
                  :disabled="isLoading"
                  @click="onResendClick"
                  >재발송</void-anchor
                >
              </div>
            </div>
            <div class="input_area" style="margin-top: 10px">
              <div class="input_group">
                <input
                  type="text"
                  v-model="answer"
                  :disabled="isLoading || !secret"
                />
                <span class="timer" v-if="!!secret">{{
                  remainingSeconds
                }}</span>
                <void-anchor
                  class="btn btn_light_gray_solid primary"
                  :disabled="isLoading || !secret"
                  @click="onVerifyClick"
                  >인증 완료</void-anchor
                >
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.popup-cont {
  padding-bottom: 30px;
}
.input_area {
  width: 418px;
}
.input_group {
  display: flex;
}
.input_group > input {
  width: 180px !important;
  flex: 1;
  margin-right: 10px;
}
.input_group > .btn {
  text-align: center;
  width: 140px !important;
  display: inline-block;
  height: 50px;
  line-height: 50px;
  min-width: 70px;
  border-radius: 6px;
  font-size: 14px;
}

.input_group > .btn.primary {
  background-color: #f65227;
  color: #fff;
}

.timer {
  display: flex;
  line-height: 100%;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  margin-left: -65px;
  margin-right: 25px;
  color: #f65227;
}
@media only screen and (max-width: 768px) {
  .input_area {
    width: 100%;
  }
}
</style>
